import { Node, mergeAttributes } from '@tiptap/core'
import { ReactNodeViewRenderer } from '@tiptap/react'
import { ScreenshotNodeView } from './screenshot-node-view'
import { nanoid } from 'nanoid'
import { ActionType, InteractionPointStyles } from '@deepguide-ai/types'

export interface InteractionPoint {
  id: string
  type: ActionType
  x: number
  y: number
  width: number
  height: number
  xPercent: number
  yPercent: number
  widthPercent: number
  heightPercent: number
  style: InteractionPointStyles
  label?: string
}

export interface ScreenshotAttributes {
  id?: string
  url?: string
  width?: number
  height?: number
  initialWidth?: number
  initialHeight?: number
  align?: 'left' | 'center' | 'right'
  caption?: string
  zoomLevel?: number
  placeholderId?: string
  fromPlaceholderId?: string
  interactionPoints?: InteractionPoint[]
  step_id?: string
  alt?: string
  inline?: boolean
  cropStyle?: 'cropped' | 'full' | 'none'
  elementBox?: {
    center: { x: number; y: number }
    width: number
    height: number
  }
  selectedTopicId?: string // Topic ID for fetching step data
}


export const ScreenshotSpec = {
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,
  attrs: {
    id: { default: nanoid(10) },
    url: { default: null },
    width: { default: null },
    height: { default: null },
    initialWidth: { default: null },
    initialHeight: { default: null },
    align: { default: null },
    caption: { default: null },
    zoomLevel: { default: null },
    alt: { default: null },
    inline: { default: false },
    cropStyle: { default: null },
    step_id: { default: null },
    placeholderId: { default: null },
  },
  toDOM() { return ['div', { 'data-screenshot': '' }, 0] as [string, Record<string, any>, 0]; },
  parseDOM: [{ tag: 'div[data-screenshot]' }],
};

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    screenshot: {
      /**
       * Insert a screenshot
       */
      insertScreenshot: (attributes?: ScreenshotAttributes) => ReturnType
      /**
       * Update screenshot attributes
       */
      updateScreenshot: (attributes: Partial<ScreenshotAttributes>) => ReturnType
    }
  }
}

export const ScreenshotExtension = Node.create({
  name: 'Screenshot',

  group: 'block',

  content: 'text*',

  atom: true,

  draggable: true,

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: element => element.getAttribute('data-screenshot-id'),
        renderHTML: attributes => {
          if (!attributes.id) {
            return {}
          }
          return {
            'data-screenshot-id': attributes.id,
          }
        },
      },
      url: {
        default: '',
        parseHTML: element => element.getAttribute('src'),
        renderHTML: attributes => {
          if (!attributes.url) {
            return {}
          }
          return {
            src: attributes.url,
          }
        },
      },
      width: {
        default: null,
        parseHTML: element => {
          const width = element.getAttribute('data-width')
          return width ? parseFloat(width) : null
        },
        renderHTML: attributes => {
          if (!attributes.width) {
            return {}
          }
          return {
            'data-width': attributes.width,
          }
        },
      },
      height: {
        default: null,
        parseHTML: element => {
          const height = element.getAttribute('data-height')
          return height ? parseFloat(height) : null
        },
        renderHTML: attributes => {
          if (!attributes.height) {
            return {}
          }
          return {
            'data-height': attributes.height,
          }
        },
      },
      initialWidth: {
        default: null,
        parseHTML: element => {
          const width = element.getAttribute('data-initial-width')
          return width ? parseFloat(width) : null
        },
        renderHTML: attributes => {
          if (!attributes.initialWidth) {
            return {}
          }
          return {
            'data-initial-width': attributes.initialWidth,
          }
        },
      },
      initialHeight: {
        default: null,
        parseHTML: element => {
          const height = element.getAttribute('data-initial-height')
          return height ? parseFloat(height) : null
        },
        renderHTML: attributes => {
          if (!attributes.initialHeight) {
            return {}
          }
          return {
            'data-initial-height': attributes.initialHeight,
          }
        },
      },
      align: {
        default: 'center',
        parseHTML: element => element.getAttribute('data-align'),
        renderHTML: attributes => {
          if (!attributes.align) {
            return {}
          }
          return {
            'data-align': attributes.align,
          }
        },
      },
      caption: {
        default: '',
        parseHTML: element => element.getAttribute('data-caption'),
        renderHTML: attributes => {
          if (!attributes.caption) {
            return {}
          }
          return {
            'data-caption': attributes.caption,
          }
        },
      },
      zoomLevel: {
        default: 1,
        parseHTML: element => {
          const zoom = element.getAttribute('data-zoom-level')
          return zoom ? parseFloat(zoom) : 1
        },
        renderHTML: attributes => {
          if (!attributes.zoomLevel || attributes.zoomLevel === 1) {
            return {}
          }
          return {
            'data-zoom-level': attributes.zoomLevel,
          }
        },
      },
      placeholderId: {
        default: null,
        parseHTML: element => element.getAttribute('data-placeholder-id'),
        renderHTML: attributes => {
          if (!attributes.placeholderId) {
            return {}
          }
          return {
            'data-placeholder-id': attributes.placeholderId,
          }
        },
      },
      fromPlaceholderId: {
        default: null,
        parseHTML: element => element.getAttribute('data-from-placeholder-id'),
        renderHTML: attributes => {
          if (!attributes.fromPlaceholderId) {
            return {}
          }
          return {
            'data-from-placeholder-id': attributes.fromPlaceholderId,
          }
        },
      },
      interactionPoints: {
        default: null,
        parseHTML: element => {
          const points = element.getAttribute('data-interaction-points')
          return points ? JSON.parse(points) : null
        },
        renderHTML: attributes => {
          if (!attributes.interactionPoints) {
            return {}
          }
          return {
            'data-interaction-points': JSON.stringify(attributes.interactionPoints),
          }
        },
      },
      step_id: {
        default: '',
        parseHTML: element => element.getAttribute('data-step-id'),
        renderHTML: attributes => {
          if (!attributes.step_id) {
            return {}
          }
          return {
            'data-step-id': attributes.step_id,
          }
        },
      },
      alt: {
        default: '',
        parseHTML: element => element.getAttribute('alt'),
        renderHTML: attributes => {
          if (!attributes.alt) {
            return {}
          }
          return {
            alt: attributes.alt,
          }
        },
      },
      inline: {
        default: false,
        parseHTML: element => element.getAttribute('data-inline') === 'true',
        renderHTML: attributes => {
          if (!attributes.inline) {
            return {}
          }
          return {
            'data-inline': attributes.inline,
          }
        },
      },
      cropStyle: {
        default: 'none',
        parseHTML: element => element.getAttribute('data-crop-style'),
        renderHTML: attributes => {
          if (!attributes.cropStyle || attributes.cropStyle === 'none') {
            return {}
          }
          return {
            'data-crop-style': attributes.cropStyle,
          }
        },
      },
      elementBox: {
        default: null,
        parseHTML: element => {
          const box = element.getAttribute('data-element-box')
          return box ? JSON.parse(box) : null
        },
        renderHTML: attributes => {
          if (!attributes.elementBox) {
            return {}
          }
          return {
            'data-element-box': JSON.stringify(attributes.elementBox),
          }
        },
      },
      selectedTopicId: {
        default: '',
        parseHTML: element => element.getAttribute('data-selected-topic-id'),
        renderHTML: attributes => {
          if (!attributes.selectedTopicId) {
            return {}
          }
          return {
            'data-selected-topic-id': attributes.selectedTopicId,
          }
        },
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-screenshot-id]',
      },
      {
        tag: 'div[data-type="Screenshot"]',
      },
      {
        tag: 'img[data-type="Screenshot"]',
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, { 'data-type': 'Screenshot' }),
      0,
    ]
  },

  addNodeView() {
    return ReactNodeViewRenderer(ScreenshotNodeView)
  },

  addCommands() {
    return {
      insertScreenshot:
        (attributes = {}) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: {
              id: `screenshot-${Date.now()}`,
              align: 'center',
              zoomLevel: 1,
              alt: 'Screenshot',
              inline: false,
              cropStyle: 'none',
              ...attributes,
            },
            content: [
              {
                type: 'text',
                text: '',
              },
            ],
          })
        },
      updateScreenshot:
        (attributes) =>
        ({ commands }) => {
          return commands.updateAttributes(this.name, attributes)
        },
    }
  },

  addKeyboardShortcuts() {
    return {
      // Support zoom controls
      '+': ({ editor }) => {
        const { selection } = editor.state
        const node = editor.state.doc.nodeAt(selection.from)
        
        if (node?.type.name === this.name) {
          const currentZoom = node.attrs.zoomLevel || 1
          const newZoom = Math.min(10, currentZoom + 0.25)
          editor.commands.updateAttributes(this.name, { zoomLevel: newZoom })
          return true
        }
        return false
      },
      '-': ({ editor }) => {
        const { selection } = editor.state
        const node = editor.state.doc.nodeAt(selection.from)
        
        if (node?.type.name === this.name) {
          const currentZoom = node.attrs.zoomLevel || 1
          const newZoom = Math.max(1, currentZoom - 0.25)
          editor.commands.updateAttributes(this.name, { zoomLevel: newZoom })
          return true
        }
        return false
      },
      '1': ({ editor }) => {
        const { selection } = editor.state
        const node = editor.state.doc.nodeAt(selection.from)
        
        if (node?.type.name === this.name) {
          editor.commands.updateAttributes(this.name, { zoomLevel: 1 })
          return true
        }
        return false
      },
    }
  },
}) 